/**
 * Tenant entitlements context: fetches GET /api/v1/entitlements on mount and exposes
 * plan, flags, limits, useFeature, and useLimit helpers.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { fetchEntitlements, normalizeLimitEntry } from '../../api/entitlementsApi';
import { LEGACY_FEATURE_ALIASES } from './keys';

const EntitlementsContext = createContext(null);

const DEFAULT_STATE = {
  plan: '',
  flags: [],
  limits: {},
  version: null,
  loading: true,
  error: null,
};

const ENTITLEMENTS_TTL_MS = 60 * 1000;

function resolveFeatureKey(flagKey) {
  return LEGACY_FEATURE_ALIASES[flagKey] || flagKey;
}

export function EntitlementsProvider({ children }) {
  const [state, setState] = useState(DEFAULT_STATE);
  const cancelledRef = useRef(false);
  const ttlTimerRef = useRef(null);

  const loadEntitlements = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    const result = await fetchEntitlements();
    if (cancelledRef.current) return;
    setState({
      plan: result.plan,
      flags: result.flags,
      limits: result.limits,
      version: result.version,
      loading: false,
      error: null,
    });
    if (ttlTimerRef.current) clearTimeout(ttlTimerRef.current);
    ttlTimerRef.current = setTimeout(loadEntitlements, ENTITLEMENTS_TTL_MS);
  }, []);

  const refetchEntitlements = useCallback(async () => {
    if (ttlTimerRef.current) {
      clearTimeout(ttlTimerRef.current);
      ttlTimerRef.current = null;
    }
    await loadEntitlements();
  }, [loadEntitlements]);

  useEffect(() => {
    cancelledRef.current = false;
    loadEntitlements();
    return () => {
      cancelledRef.current = true;
      if (ttlTimerRef.current) clearTimeout(ttlTimerRef.current);
    };
  }, [loadEntitlements]);

  const value = useMemo(() => {
    const { flags, limits, plan, version, loading, error } = state;

    const isEnabled = (name) => {
      const key = resolveFeatureKey(name);
      return flags.some((f) => f.name === key);
    };

    const getParams = (name) => {
      const key = resolveFeatureKey(name);
      const f = flags.find((x) => x.name === key);
      return f ? f.params : {};
    };

    const getLimitEntry = (limitKey) => {
      if (!limitKey || !limits[limitKey]) {
        return { value: 0, unlimited: true };
      }
      return normalizeLimitEntry(limits[limitKey]);
    };

    const getLimitCap = (limitKey) => {
      const entry = getLimitEntry(limitKey);
      return entry.unlimited ? null : entry.value;
    };

    const canAddWithinLimit = (limitKey, currentUsage, requestedDelta = 1) => {
      const cap = getLimitCap(limitKey);
      if (cap == null) return true;
      return currentUsage + requestedDelta <= cap;
    };

    return {
      plan,
      flags,
      limits,
      version,
      loading,
      error,
      isEnabled,
      getParams,
      getLimitEntry,
      getLimitCap,
      canAddWithinLimit,
      refetchEntitlements,
    };
  }, [state, refetchEntitlements]);

  return (
    <EntitlementsContext.Provider value={value}>{children}</EntitlementsContext.Provider>
  );
}

export function useEntitlements() {
  const ctx = useContext(EntitlementsContext);
  return {
    plan: ctx?.plan ?? '',
    flags: ctx?.flags ?? [],
    limits: ctx?.limits ?? {},
    version: ctx?.version ?? null,
    loading: ctx?.loading ?? false,
    error: ctx?.error ?? null,
    isEnabled: ctx?.isEnabled ?? (() => false),
    getParams: ctx?.getParams ?? (() => ({})),
    getLimitEntry: ctx?.getLimitEntry ?? (() => ({ value: 0, unlimited: true })),
    getLimitCap: ctx?.getLimitCap ?? (() => null),
    canAddWithinLimit: ctx?.canAddWithinLimit ?? (() => true),
    refetchEntitlements: ctx?.refetchEntitlements ?? (async () => {}),
  };
}

export function useFeature(flagKey) {
  const { isEnabled, getParams, plan, loading } = useEntitlements();
  const enabled = isEnabled(flagKey);
  return {
    enabled,
    params: getParams(flagKey),
    plan,
    loading,
  };
}

export function useLimit(limitKey) {
  const { getLimitEntry, getLimitCap, canAddWithinLimit, plan, loading } = useEntitlements();
  const entry = getLimitEntry(limitKey);
  const cap = getLimitCap(limitKey);
  return {
    value: entry.value,
    unlimited: entry.unlimited,
    cap,
    plan,
    loading,
    canAdd: (currentUsage, requestedDelta = 1) =>
      canAddWithinLimit(limitKey, currentUsage, requestedDelta),
    isAtCap: (currentUsage) => cap != null && currentUsage >= cap,
  };
}
